import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithHttpStatus } from '../services/baseQuery';
import ApiMiddleWare from '../api-middleware';
import { logError } from '../../util/telemetry/error/log-error.util';
import { GetKioskFlightsParams, GetKioskFlightsResponse, GetKioskFlightReportParams, GetKioskFlightReportResponse, GetKioskUserProfileResponse, UpdateKioskProfileParams, UpdateKioskProfileResponse, CheckDuplicateRunParams, CheckDuplicateRunResponse, SubmitReputationCheckParams, SubmitReputationCheckResponse, GetRunEligibilityResponse, SubmitKioskChatMessageParams, SubmitKioskChatMessageResponse, SendKioskFlightReportEmailResponse, SendKioskFlightReportEmailParams } from '../../kiosk/types/kiosk.types';

// Define a service using a base URL and expected endpoints
export const kioskStore = createApi({
    reducerPath: 'kioskStore',
    baseQuery: baseQueryWithHttpStatus,
      tagTypes: ['kioskFlights', 'duplicateChecks', 'kioskUserProfile'],
    endpoints: (builder) => ({
        getCompanyThemeDetails: builder.mutation({
            query: (body) => {
                return {
                    url: "kioskBranding/getCompanyThemeDetails/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        checkUserExists: builder.mutation({
            query: (body) => {
                return {
                    url: "kioskBranding/checkUserExists/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        submitReputationCheck: builder.mutation<SubmitReputationCheckResponse, SubmitReputationCheckParams>({
            query: (body) => {
                return {
                    url: "kioskBranding/submitReputationCheck/",
                    method: "POST",
                    body: { ...body }
                }
            },
            invalidatesTags: ['duplicateChecks', 'kioskFlights'],
        }),
        kioskVerifyOTP: builder.mutation({
            query: (body) => {
                return {
                    url: "kiosk/verifyOTP/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        kioskResendOTP: builder.mutation({
            query: (body) => {
                return {
                    url: "kiosk/resendOTP/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        acceptConsent: builder.mutation({
            query: (body) => {
                return {
                    url: "kiosk/acceptConsent/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        getPhoneVerificationDetails: builder.mutation({
            query: (body) => {
                return {
                    url: "kioskBranding/getPhoneVerificationDetails/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        getKioskFlights: builder.query<GetKioskFlightsResponse, GetKioskFlightsParams>({
            query: (params) => ({
                url: "kioskBranding/getKioskFlights/",
                method: "GET",
                params: params
            }),
            providesTags: ['kioskFlights'],
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getKioskFlights query failed' }); 
                } 
            }
        }),
        getKioskFlightReport: builder.query<GetKioskFlightReportResponse, GetKioskFlightReportParams>({
            query: (params) => ({
                url: "kioskBranding/getKioskFlightReport/",
                method: "GET",
                params: params
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getKioskFlightReport query failed' }); 
                } 
            }
        }),
        getKioskUserProfile: builder.query<GetKioskUserProfileResponse, void>({
            query: () => ({
                url: "kioskBranding/getKioskUserProfile/",
                method: "GET"
            }),
            providesTags: ['kioskUserProfile'],
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getKioskUserProfile query failed' }); 
                } 
            }
        }),
        updateKioskProfile: builder.mutation<UpdateKioskProfileResponse, UpdateKioskProfileParams>({
            query: (body) => ({
                url: "kioskBranding/updateKioskProfile/",
                method: "POST",
                body: { ...body }
            }),
            invalidatesTags: ['kioskUserProfile'],
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'updateKioskProfile query failed' }); 
                } 
            }
        }),
        checkDuplicateRun: builder.query<CheckDuplicateRunResponse, CheckDuplicateRunParams>({
            query: (body) => ({
                url: "kioskBranding/checkDuplicateRun/",
                method: "POST",
                body: body
            }),
            providesTags: ['duplicateChecks'],
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'checkDuplicateRun query failed' }); 
                } 
            },
        }),
        getRunEligibility: builder.query<GetRunEligibilityResponse, void>({
            query: () => ({
                url: "kiosk/runEligibility/",
                method: "GET"
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getRunEligibility query failed' }); 
                } 
            },
        }),
        submitKioskChatMessage: builder.mutation<SubmitKioskChatMessageResponse, SubmitKioskChatMessageParams>({
            query: (body) => ({
                url: "kiosk/submitKioskChatMessage/",
                method: "POST",
                body
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'submitKioskChatMessage query failed' }); 
                } 
            },
        }),
        sendKioskFlightReportEmail: builder.mutation<SendKioskFlightReportEmailResponse, SendKioskFlightReportEmailParams>({
            query: (body) => ({
                url: "kioskBranding/sendKioskFlightReportEmail/",
                method: "POST",
                body
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'sendKioskFlightReportEmail query failed' }); 
                } 
            },
        }),
    }),
})

export const {
    useGetCompanyThemeDetailsMutation,
    useCheckUserExistsMutation,
    useSubmitReputationCheckMutation,
    useKioskVerifyOTPMutation,
    useKioskResendOTPMutation,
    useAcceptConsentMutation,
    useGetPhoneVerificationDetailsMutation,
    useGetKioskFlightsQuery,
    useGetKioskFlightReportQuery,
    useGetKioskUserProfileQuery,
    useUpdateKioskProfileMutation,
    useCheckDuplicateRunQuery,
    useGetRunEligibilityQuery,
    useSubmitKioskChatMessageMutation,
    useSendKioskFlightReportEmailMutation,
    useLazyGetKioskFlightReportQuery
} = kioskStore;

